import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { fg } from '@confluence/feature-gating';
import { LoadableLazy, type LoadableType } from '@confluence/loadable';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import {
	AttachmentActionExtensionProvider,
	type AttachmentActionEligibilityResult,
	type AttachmentActionExtensionProviderProps,
} from '../AttachmentActionExtensionProvider.tsx';
import type { AttachmentActionDiscoveryExperienceStatus } from '../useAttachmentActionDiscoveryExperience.ts';
import type { AttachmentActionDiscoverySuccessReason } from '../AttachmentActionDiscoveryExperienceCoordinator.ts';
import {
	AttachmentActionEligibilityLoadStateContext,
	DISABLED_ATTACHMENT_ACTION_LOAD_STATE,
	type AttachmentActionLoadState,
} from '../AttachmentActionLoadState.ts';

import type { AttachmentActionEligibilityQueryProps } from './AttachmentActionEligibilityQuery.tsx';
import { toAttachmentActionAri } from './attachmentActionAris.ts';
import {
	AttachmentActionEligibilityContext,
	EMPTY_ACTIONS_BY_ATTACHMENT_ID,
	type AttachmentActionEligibilityData,
} from './AttachmentActionEligibilityContext.ts';

type EligibilityResult = AttachmentActionEligibilityResult & AttachmentActionEligibilityData;

type Props = Omit<
	AttachmentActionExtensionProviderProps,
	'attachmentIds' | 'attachmentActionEligibilityResult'
> & { attachmentIds: ReadonlyArray<string> };

const EMPTY_RESULT: EligibilityResult = {
	descriptorsByAttachmentId: new Map(),
	status: 'loading',
	actionsByAttachmentId: EMPTY_ACTIONS_BY_ATTACHMENT_ID,
};

const DiscoveryLoading = ({
	error,
	onDiscoveryStatusChange,
}: AttachmentActionEligibilityQueryProps & { error?: unknown }) => {
	useEffect(() => {
		if (error) {
			onDiscoveryStatusChange('failure');
		}
	}, [error, onDiscoveryStatusChange]);
	return null;
};

const DiscoveryLoader: LoadableType<AttachmentActionEligibilityQueryProps> = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AttachmentActionEligibilityDiscovery" */ './AttachmentActionEligibilityDiscovery.tsx'
			)
		).AttachmentActionEligibilityDiscovery,
	isReactLoadable: true,
	loading: DiscoveryLoading,
});

const AttachmentSpecificDiscovery = (
	props: AttachmentActionEligibilityQueryProps,
): React.JSX.Element | null =>
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	fg('forge_attachment_display_conditions') ? <DiscoveryLoader {...props} /> : null;

/** One optional, client-only eligibility batch for the visible attachment rows. */
export const AttachmentActionEligibilityProvider = ({
	children,
	...props
}: Props): React.JSX.Element => {
	const { attachmentIds, content, space, surface } = props;
	const { cloudId } = useSessionData();
	const { locale } = useIntl();
	const renderServerPlaceholder = useIsSSROrHydrating();
	const contentAri =
		props.contentAri ??
		(content.id && cloudId ? toAttachmentActionAri(content.id, cloudId, 'content') : null);
	const targetKey = useMemo(
		() =>
			JSON.stringify([contentAri, attachmentIds, cloudId, locale, space.id, space.key, surface]),
		[attachmentIds, cloudId, contentAri, locale, space.id, space.key, surface],
	);
	const [discovery, setDiscovery] = useState<{
		targetKey: string;
		result: EligibilityResult;
	}>();
	const handleEligibility = useCallback(
		(eligibility: AttachmentActionEligibilityData) => {
			setDiscovery((current) => ({
				targetKey,
				result: {
					...(current?.targetKey === targetKey ? current.result : EMPTY_RESULT),
					...eligibility,
				},
			}));
		},
		[targetKey],
	);
	const handleStatus = useCallback(
		(
			status: AttachmentActionDiscoveryExperienceStatus,
			successReason?: AttachmentActionDiscoverySuccessReason,
		) => {
			setDiscovery((current) => ({
				targetKey,
				result: {
					...(current?.targetKey === targetKey ? current.result : EMPTY_RESULT),
					status,
					successReason,
				},
			}));
		},
		[targetKey],
	);

	const enabled = Boolean(
		!renderServerPlaceholder &&
		contentAri &&
		content.id &&
		content.type &&
		space.id &&
		space.key &&
		attachmentIds.length > 0 &&
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		(fg('forge_attachment_action_module_metadata') || fg('forge_attachment_action')),
	);
	const result = discovery?.targetKey === targetKey ? discovery.result : EMPTY_RESULT;
	const loadState = useMemo<AttachmentActionLoadState>(
		() => ({
			status: result.status,
			hasExtensions:
				result.status === 'success' &&
				Array.from(result.actionsByAttachmentId.values()).some((actions) => actions.length > 0),
		}),
		[result],
	);

	return (
		<AttachmentActionExtensionProvider
			{...props}
			contentAri={contentAri}
			attachmentActionEligibilityResult={result}
		>
			{enabled && contentAri && (
				<AttachmentSpecificDiscovery
					attachmentIds={attachmentIds}
					contentAri={contentAri}
					onEligibility={handleEligibility}
					onDiscoveryStatusChange={handleStatus}
				/>
			)}
			<AttachmentActionEligibilityContext.Provider
				value={
					enabled && result.status === 'success'
						? result.actionsByAttachmentId
						: EMPTY_ACTIONS_BY_ATTACHMENT_ID
				}
			>
				<AttachmentActionEligibilityLoadStateContext.Provider
					value={
						// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- Telemetry observes the existing independent action and eligibility gates; it does not introduce feature preconditions.
						enabled && fg('forge_attachment_display_conditions') && fg('forge_attachment_action')
							? loadState
							: DISABLED_ATTACHMENT_ACTION_LOAD_STATE
					}
				>
					{children}
				</AttachmentActionEligibilityLoadStateContext.Provider>
			</AttachmentActionEligibilityContext.Provider>
		</AttachmentActionExtensionProvider>
	);
};
